import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client.js';
import { useApp } from '../store/AppContext.js';
import { Alert } from '../components/ui.js';
import { generateRecoveryKey, normalizeRecoveryKey, deriveRecoveryKey, wrapKey } from '@battini/crypto';

export function AccountSettings() {
  const { user, security, reloadSecurity, vaultSession } = useApp();
  const [timeout, setTimeoutSec] = useState(security?.sessionTimeoutSeconds ?? 900);
  const [autoLock, setAutoLock] = useState(security?.autoLockOnIdle ?? true);
  const [clip, setClip] = useState(security?.clipboardClearSeconds ?? 30);
  const [msg, setMsg] = useState<string | null>(null);
  const [recoveryEnrolled, setRecoveryEnrolled] = useState(security?.recoveryKeyEnrolled ?? false);
  const [recoveryKey, setRecoveryKey] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ enrolled: boolean }>('/auth/recovery-key').then((r) => setRecoveryEnrolled(r.body.enrolled)).catch(() => undefined);
  }, []);

  const save = async () => {
    setMsg(null);
    try {
      await api.put('/auth/security-settings', { sessionTimeoutSeconds: timeout, autoLockOnIdle: autoLock, clipboardClearSeconds: clip });
      await reloadSecurity();
      setMsg('Settings saved.');
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Failed');
    }
  };

  const enrollRecovery = async () => {
    if (!vaultSession) return;
    const key = generateRecoveryKey();
    const rk = await deriveRecoveryKey(normalizeRecoveryKey(key));
    const protectedRecoveryKey = await wrapKey(rk, vaultSession.symKey);
    await api.post('/auth/recovery-key', { protectedRecoveryKey });
    setRecoveryEnrolled(true);
    setRecoveryKey(key);
  };

  return (
    <div className="grid" style={{ maxWidth: 720 }}>
      <div className="panel">
        <h3 style={{ marginTop: 0 }}>Account</h3>
        <div className="flex between"><span className="muted">Email</span><strong>{user?.email}</strong></div>
        <div className="flex between mt-sm"><span className="muted">Status</span><span className="muted">{user?.status}</span></div>
        <div className="flex between mt-sm"><span className="muted">Member since</span><span className="muted">{user ? new Date(user.createdAt).toLocaleDateString() : '—'}</span></div>
      </div>

      <div className="panel">
        <h3 style={{ marginTop: 0 }}>Two-factor authentication</h3>
        <div className="flex between">
          <span className="muted">TOTP authenticator</span>
          <span className={`badge ${security?.mfaEnabled ? 'success' : 'warn'}`}>{security?.mfaEnabled ? 'Enabled' : 'Disabled'}</span>
        </div>
        <Link className="btn secondary sm mt" to="/settings/mfa">{security?.mfaEnabled ? 'Manage MFA' : 'Set up MFA'}</Link>
      </div>

      <div className="panel">
        <h3 style={{ marginTop: 0 }}>Recovery key</h3>
        {recoveryKey ? (
          <Alert kind="warn">
            <strong>Save this recovery key now — it is shown only once.</strong>
            <div className="mono" style={{ marginTop: 8, wordBreak: 'break-all', fontSize: 13 }}>{recoveryKey}</div>
            Store it offline (e.g. a password manager or printed). If you lose your master password, this key is the only way to recover your vault.
          </Alert>
        ) : recoveryEnrolled ? (
          <p className="muted" style={{ fontSize: 13 }}>✓ A recovery key is enrolled. Your vault can be recovered with it if you lose your master password.</p>
        ) : (
          <>
            <p className="muted" style={{ fontSize: 13 }}>Without a recovery key, losing your master password means your vault is permanently unrecoverable.</p>
            <button className="btn secondary sm" onClick={enrollRecovery}>Enroll recovery key</button>
          </>
        )}
      </div>

      <div className="panel">
        <h3 style={{ marginTop: 0 }}>Session & locking</h3>
        {msg && <Alert kind="info">{msg}</Alert>}
        <div className="field">
          <label>Auto-lock after (seconds): <strong>{timeout}</strong></label>
          <input type="range" min={60} max={3600} step={60} value={timeout} onChange={(e) => setTimeoutSec(Number(e.target.value))} />
        </div>
        <label className="flex"><input type="checkbox" checked={autoLock} onChange={(e) => setAutoLock(e.target.checked)} style={{ marginRight: 8 }} /> Lock when idle</label>
        <div className="field mt">
          <label>Clear clipboard after (seconds)</label>
          <input type="number" min={0} max={600} value={clip} onChange={(e) => setClip(Number(e.target.value))} />
        </div>
        <button className="btn" onClick={save}>Save settings</button>
      </div>
    </div>
  );
}
